import { ALL_PRODUCT_TYPES, MULTI_PRODUCT_DEFAULT_COLORS } from "constants/chart.constants";
import { MultiProductFeature } from "types/charts/feature";
import { seriesMatchesProduct } from "utils/chart/seriesMatchesProduct";

// ALL_PRODUCT_TYPES is a static constant; resolve its entries once at module load
// rather than on every call (this runs per-series and on every hover/spaghetti interaction).
const ALL_PRODUCT_ENTRIES = Object.values(ALL_PRODUCT_TYPES) as Array<{
  key: string;
  label: string;
}>;

interface ColorableSeries {
  style?: { hexColor?: string };
  isForecast?: boolean;
  isComparisonForecast?: boolean;
  label?: string;
  groupTitle?: string;
}

/** Perf suffix used to key forecast/backfit colours in MULTI_PRODUCT_DEFAULT_COLORS. */
export type ProductColorSuffix = "" | "-fcst" | "-backfit";

/**
 * Resolves a product's "Override bin colours" colour from MULTI_PRODUCT_DEFAULT_COLORS,
 * mapping the product display label or key to its ALL_PRODUCT_TYPES key. Returns undefined
 * when the product has no entry in the table. Shared by getEffectiveSeriesColor (chart
 * lines) and the MPC product legend so both resolve identical colours.
 */
export function getProductOverrideColor(
  product: string,
  suffix: ProductColorSuffix = ""
): string | undefined {
  const productEntry =
    ALL_PRODUCT_ENTRIES.find((p) => p.label === product) ??
    ALL_PRODUCT_ENTRIES.find((p) => p.key === product);
  const key = (productEntry?.key ?? product).toLowerCase();
  return MULTI_PRODUCT_DEFAULT_COLORS[key + suffix];
}

/**
 * Returns the effective colour for a series, respecting the "Override bin colours"
 * toggle on the new MPC.
 *
 * When overrideBinColors is OFF (or multiProduct is absent) → returns series.style.hexColor
 * as-is (the server-assigned bin colour).
 *
 * When overrideBinColors is ON → resolves the series' product from the primary+secondary
 * product lists (same loop used by getLineStyleForSeries), maps it to the ALL_PRODUCT_TYPES
 * key, derives a perf suffix from isForecast / isComparisonForecast, and looks up
 * MULTI_PRODUCT_DEFAULT_COLORS. Falls back to series.style.hexColor for any product that
 * has no entry in the table (e.g. unmapped or midstream-only products).
 *
 * Only COLOUR changes here. Line style/dash (getLineStyleForSeries) is untouched.
 */
export function getEffectiveSeriesColor(
  series: ColorableSeries,
  multiProduct?: Maybe<Partial<MultiProductFeature>>
): string {
  const fallback = series?.style?.hexColor ?? "#000";

  if (!multiProduct?.properties?.overrideBinColors) {
    return fallback;
  }

  const primaryProducts: string[] = multiProduct?.properties?.primaryProducts ?? [];
  const secondaryProducts: string[] = multiProduct?.properties?.secondaryProducts ?? [];
  const allProducts = [...primaryProducts, ...secondaryProducts];

  // Find the first product this series matches (same loop as getLineStyleForSeries).
  let matchedProduct: string | null = null;
  for (const product of allProducts) {
    if (seriesMatchesProduct(series, product)) {
      matchedProduct = product;
      break;
    }
  }

  if (matchedProduct === null) {
    // No product matched — keep bin colour.
    return fallback;
  }

  // Derive perf suffix: comparison forecasts are "-backfit", regular forecasts are "-fcst".
  let suffix: ProductColorSuffix = "";
  if (series.isComparisonForecast) {
    suffix = "-backfit";
  } else if (series.isForecast) {
    suffix = "-fcst";
  }

  return getProductOverrideColor(matchedProduct, suffix) ?? fallback;
}
